import { useState, useEffect } from 'react';
import { 
    CalendarIcon, 
    ClockIcon, 
    UsersIcon, 
    ArrowLeftIcon, 
    UserIcon,
    DocumentTextIcon,
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import getAppointmentHistoryById from '../api/getAppointmentHistoryById';
import { WhiteCardContainer, FormFieldsContainer } from '../../../components';
import ProfileFormHeader from './ProfileFormHeader/ProfileFormHeader';

const statusNames = {
  1: 'Pendiente',
  2: 'Completada',
  3: 'Cancelada por doctor',
  4: 'Cancelada por usuario'
};

const statusColors = {
  1: 'bg-yellow-100 text-yellow-800',
  2: 'bg-green-100 text-green-800',
  3: 'bg-red-100 text-red-800',
  4: 'bg-red-100 text-red-800'
};

const actionNames = {
  CREATED: 'Creada',
  UPDATED: 'Actualizada',
  CANCELLED: 'Cancelada',
  COMPLETED: 'Completada'
};

const actionColors = {
  CREATED: 'bg-blue-100 text-blue-800 border-blue-200',
  UPDATED: 'bg-amber-100 text-amber-800 border-amber-200',
  CANCELLED: 'bg-red-100 text-red-800 border-red-200',
  COMPLETED: 'bg-green-100 text-green-800 border-green-200'
};

const actionIcons = {
  CREATED: CheckCircleIcon,
  UPDATED: InformationCircleIcon,
  CANCELLED: XCircleIcon,
  COMPLETED: CheckCircleIcon
};

export default function PatientAppointmentHistory({ patient, doctor, onBack }) {
    const [appointments, setAppointments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const fetchAppointmentHistory = async () => {
            if (patient?.appointmentId) {
                setIsLoading(true);
                setErrorMessage('');
                
                try {
                    const history = await getAppointmentHistoryById(
                        patient.appointmentId, 
                        setErrorMessage
                    );
                    
                    if (history) {
                        setAppointments(history);
                    }
                } catch (error) {
                    setErrorMessage('Error al cargar el historial de citas');
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchAppointmentHistory();
    }, [patient?.appointmentId]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        const date = new Date(dateTimeString);
        return date.toLocaleString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusChangeText = (historyItem) => {
        if (historyItem.oldStatusId && historyItem.newStatusId) {
            return `Estado cambió de "${statusNames[historyItem.oldStatusId] || 'Desconocido'}" a "${statusNames[historyItem.newStatusId] || 'Desconocido'}"`;
        } else if (historyItem.newStatusId) {
            return `Estado establecido como "${statusNames[historyItem.newStatusId] || 'Desconocido'}"`;
        }
        return 'Sin cambios de estado';
    };

    if (isLoading) {
        return (
            <WhiteCardContainer>
                <FormFieldsContainer>
                    <ProfileFormHeader title="Cargando historial..." />
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dl-primary-900"></div>
                    </div>
                </FormFieldsContainer>
            </WhiteCardContainer>
        );
    }

    if (errorMessage) {
        return (
            <WhiteCardContainer>
                <FormFieldsContainer>
                    <ProfileFormHeader title="Error" />
                    <div className="text-center py-8">
                        <p className="text-red-600 mb-4">{errorMessage}</p>
                        <button
                            onClick={onBack}
                            className="bg-dl-primary-900 text-white px-4 py-2 rounded-full hover:bg-dl-primary-700"
                        >
                            Volver
                        </button>
                    </div>
                </FormFieldsContainer>
            </WhiteCardContainer>
        );
    }

    return (
        <WhiteCardContainer>
            <FormFieldsContainer>
                <div className="flex items-center justify-between mb-6">
                    <ProfileFormHeader 
                        title={`Historial de Citas`}
                        subtitle={`Dr. ${doctor?.doctorFullName || 'Médico'}`}
                    />
                    <button
                        onClick={onBack}
                        className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dl-accent"
                    >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Volver
                    </button>
                </div>

                {appointments.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <CalendarIcon className="h-12 w-12 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay historial registrado</h3>
                        <p className="text-sm text-gray-500 max-w-sm mx-auto">
                            No se encontró historial de cambios para esta cita.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Appointment Info Card */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                            <div className="flex items-center mb-4">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                    <CalendarIcon className="h-6 w-6 text-blue-600" />
                                </div>
                                <h4 className="text-lg font-semibold text-gray-900">Información de la Cita</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="flex items-center space-x-3">
                                    <UserIcon className="h-5 w-5 text-gray-400" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">Paciente</p>
                                        <p className="text-sm text-gray-900">{appointments[0]?.userFullName || patient?.userFullName}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <UsersIcon className="h-5 w-5 text-gray-400" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">Doctor</p>
                                        <p className="text-sm text-gray-900">{appointments[0]?.doctorFullName || doctor?.doctorFullName}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <ClockIcon className="h-5 w-5 text-gray-400" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">Fecha</p>
                                        <p className="text-sm text-gray-900">{formatDate(appointments[0]?.appDate)}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">Motivo</p>
                                        <p className="text-sm text-gray-900">{appointments[0]?.motiveName || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <UserIcon className="h-5 w-5 text-gray-400" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">Email</p>
                                        <p className="text-sm text-gray-900">{appointments[0]?.userEmail || patient?.userEmail}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <ClockIcon className="h-5 w-5 text-gray-400" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">Teléfono</p>
                                        <p className="text-sm text-gray-900">{appointments[0]?.userPhone || patient?.userPhone}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Timeline Header */}
                        <div className="flex items-center justify-between">
                            <h4 className="text-xl font-semibold text-gray-900 flex items-center">
                                <ClockIcon className="h-6 w-6 text-gray-600 mr-2" />
                                Historial de Cambios
                            </h4>
                            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                                {appointments.length} {appointments.length === 1 ? 'evento' : 'eventos'}
                            </span>
                        </div>

                        {/* Timeline */}
                        <div className="relative">
                            {/* Timeline line */}
                            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                            
                            <div className="space-y-6">
                                {appointments.map((historyItem, index) => {
                                    const ActionIcon = actionIcons[historyItem.action] || InformationCircleIcon;
                                    const isLast = index === appointments.length - 1;
                                    
                                    return (
                                        <div key={historyItem.historyId || index} className="relative flex items-start space-x-4">
                                            {/* Timeline dot */}
                                            <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full border-2 border-white flex items-center justify-center ${
                                                historyItem.action === 'CREATED' ? 'bg-blue-500' :
                                                historyItem.action === 'UPDATED' ? 'bg-amber-500' :
                                                historyItem.action === 'CANCELLED' ? 'bg-red-500' :
                                                'bg-green-500'
                                            }`}>
                                                <ActionIcon className="h-4 w-4 text-white" />
                                            </div>
                                            
                                            {/* Content card */}
                                            <div className={`flex-1 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 ${
                                                isLast ? 'border-blue-200 bg-blue-50/30' : ''
                                            }`}>
                                                <div className="p-6">
                                                    {/* Header */}
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className="flex items-center space-x-3">
                                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${actionColors[historyItem.action] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                                                                {actionNames[historyItem.action] || historyItem.action}
                                                            </span>
                                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColors[historyItem.newStatusId] || 'bg-gray-100 text-gray-800'}`}>
                                                                {statusNames[historyItem.newStatusId] || 'Estado desconocido'}
                                                            </span>
                                                        </div>
                                                        <div className="text-sm text-gray-500 flex items-center">
                                                            <ClockIcon className="h-4 w-4 mr-1" />
                                                            {formatDateTime(historyItem.changedAt)}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Content */}
                                                    <div className="space-y-4">
                                                        {/* Who made the change */}
                                                        <div className="flex items-center space-x-2 text-sm">
                                                            <UserIcon className="h-4 w-4 text-gray-400" />
                                                            <span className="font-medium text-gray-700">Realizado por:</span>
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                                historyItem.changedByRole === 'doctor' 
                                                                    ? 'bg-blue-100 text-blue-800' 
                                                                    : 'bg-green-100 text-green-800'
                                                            }`}>
                                                                {historyItem.changedByRole === 'doctor' ? 'Doctor' : 'Usuario'}
                                                            </span>
                                                            <span className="text-gray-500">(ID: {historyItem.changedBy})</span>
                                                        </div>
                                                        
                                                        {/* Status change */}
                                                        <div className="text-sm">
                                                            <span className="font-medium text-gray-700">Cambio de estado:</span>
                                                            <p className="text-gray-600 mt-1">{getStatusChangeText(historyItem)}</p>
                                                        </div>
                                                        
                                                        {/* Notes sections */}
                                                        {historyItem.newDoctorNotes && (
                                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                                <div className="flex items-center space-x-2 mb-2">
                                                                    <UsersIcon className="h-4 w-4 text-blue-600" />
                                                                    <span className="font-medium text-blue-900">Notas del Doctor</span>
                                                                </div>
                                                                <p className="text-blue-800 text-sm">{historyItem.newDoctorNotes}</p>
                                                            </div>
                                                        )}
                                                        
                                                        {historyItem.newUserNotes && (
                                                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                                                <div className="flex items-center space-x-2 mb-2">
                                                                    <UserIcon className="h-4 w-4 text-green-600" />
                                                                    <span className="font-medium text-green-900">Notas del Paciente</span>
                                                                </div>
                                                                <p className="text-green-800 text-sm">{historyItem.newUserNotes}</p>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Previous notes if changed */}
                                                        {(historyItem.oldDoctorNotes && historyItem.oldDoctorNotes !== historyItem.newDoctorNotes) && (
                                                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                                                <div className="flex items-center space-x-2 mb-2">
                                                                    <ExclamationTriangleIcon className="h-4 w-4 text-gray-500" />
                                                                    <span className="font-medium text-gray-700">Notas Anteriores del Doctor</span>
                                                                </div>
                                                                <p className="text-gray-600 text-sm line-through">{historyItem.oldDoctorNotes}</p>
                                                            </div>
                                                        )}
                                                        
                                                        {(historyItem.oldUserNotes && historyItem.oldUserNotes !== historyItem.newUserNotes) && (
                                                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                                                <div className="flex items-center space-x-2 mb-2">
                                                                    <ExclamationTriangleIcon className="h-4 w-4 text-gray-500" />
                                                                    <span className="font-medium text-gray-700">Notas Anteriores del Paciente</span>
                                                                </div>
                                                                <p className="text-gray-600 text-sm line-through">{historyItem.oldUserNotes}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </FormFieldsContainer>
        </WhiteCardContainer>
    );
}
